import { Box, Paper, useTheme } from "@mui/material";
import { mdHeight, xsHeight } from "components/AppBar";
import { ReactNode } from "react";

/**
 * The shared chrome for a waste list page: a white card floating on the app's
 * canvas, the way the waste specs draw every list.
 *
 * The canvas is what gives the card an edge — on a white page the grid has
 * nothing to sit on and the header, tiles and rows read as one undifferentiated
 * column. The card carries no padding of its own on the horizontal axis:
 * OmnisearchDataGridV2 already insets its toolbar and rows, so padding here
 * would double it. Callers pad anything they render above the grid to match.
 *
 * `above` is for anything that belongs on the canvas beside the list rather
 * than inside its card — a dashboard, a summary. It sits on the canvas at the
 * card's width and brings its own card and bottom gutter.
 */
export function WasteListPageSurface({
  children,
  above,
}: {
  children: ReactNode;
  above?: ReactNode;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        backgroundColor: "canvas",
        px: 3,
        py: 3,
        // Fill the viewport so the canvas reaches the bottom of short pages
        // instead of stopping under the last row.
        minHeight: {
          xs: `calc(100vh - ${theme.spacing(xsHeight)})`,
          md: `calc(100vh - ${theme.spacing(mdHeight)})`,
        },
      }}
    >
      {above}
      <Paper elevation={0} sx={{ borderRadius: 2, py: 3 }}>
        {children}
      </Paper>
    </Box>
  );
}
